import {
  fetchInboxes,
  fetchRecentInbox,
  handleMarkStatusReport,
  useInboxStore,
} from '@/entities/inbox';
import { isC1cStudy, searchStudyFidFormatter } from '@/entities/study';
import { EMITTER_CONSTANTS } from '@/shared/config';
import {
  auth,
  emitter,
  showMessageError,
  SocketIo,
  GETTEXT_ALL_FACILITIES,
  GETTEXT_ALL_PRIORITY,
  GETTEXT_ALL_STUDIES,
} from '@/shared/lib';
import { useSetState, useUpdateEffect } from '@/shared/lib/customHooks';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatSortValue } from '../../../lib/inboxSearch';
import { ALL_REPORT_STATUS } from '../../../config/inboxList';
import { recentInboxesFormatter } from '../../../lib/recentInboxList';
import type { RecentInboxListProps } from './recentInboxList.types';

/**
 * Drives the recent inbox panel: paging, filtering, sorting, socket-driven row updates and status-report saves. Seeds a useSetState bag from searchGoto, fetches via fetchInboxes on activation and on facility, study-scope or status changes, exposes handleReloadData and fetchData on the imperative ref, listens to the inbox, mark-inbox, mark-status-report and report-updated emitter events, and memoizes displayedRecentInboxes while publishing the unread count upward.
 *
 * @param isActive - Whether this panel is the visible tab.
 * @param updateRecentUnreadCount - Callback publishing the unread count upward.
 * @param updateSearchGoto - Callback mirroring the filter state to the shared search goto.
 * @param searchGoto - Persisted search, sort and status filter values.
 * @param selectedFacilityProp - The facility option currently selected.
 * @param selectedTypeSeePatients - The all-studies or your-studies scope option.
 * @param ref - Forwarded ref receiving handleReloadData and fetchData.
 * @returns State, the derived display list, page size and the row handlers the panel renders with.
 */
const useRecentInboxList = (
  {
    isActive,
    updateRecentUnreadCount,
    updateSearchGoto,
    searchGoto,
    selectedFacility: selectedFacilityProp,
    selectedTypeSeePatients,
  }: RecentInboxListProps,
  ref: any,
) => {
  const navigate = useNavigate();

  /**
   * Subscribes to the whole inbox store so the hook can read the persisted Goto search state. Exposes search.physicianSearchValue, which drives the physician filter of the displayed list.
   *
   * @returns The full inbox store state.
   */
  const inboxesGotoRedux = useInboxStore((s) => s);

  /**
   * Selects the inbox store's updateInboxGoto action for persisting Goto search state. Bound from the store so filter changes survive navigation between inbox tabs.
   *
   * @returns The updateInboxGoto store action.
   */
  const setStateRedux = useInboxStore((s) => s.updateInboxGoto);

  const sizePerPage = useRef(10);
  const recentUnreadCountRef = useRef(0);
  const isInitial = useRef(true);
  const joinedDeviceIdsRef = useRef<any>([]);

  /**
   * Subscribes the socket connection to the device rooms of the recent inbox reports just loaded so their report-updated events arrive. Collects each report's study.deviceId, drops empty and 'undefined' values, unions them with the ids already held in joinedDeviceIdsRef, and emits a `report-updated-{deviceId}` room only for the ids that are new.
   *
   * @param reports - The report list whose device ids should be joined.
   * @returns No return value.
   */
  const joinDeviceRooms = (reports: any) => {
    // Join device rooms (unique deviceIds only)
    const validDeviceIds = reports
      .map((report: any) => report.study?.deviceId)
      .filter((deviceId: any) => deviceId && deviceId !== 'undefined');
    const updatedDeviceIds = _.union(joinedDeviceIdsRef.current, validDeviceIds);
    const newDeviceIds = _.difference(updatedDeviceIds, joinedDeviceIdsRef.current);
    joinedDeviceIdsRef.current = updatedDeviceIds;
    _.forEach(newDeviceIds, (deviceId) => {
      const deviceRoomId = `report-updated-${deviceId}`;
      (SocketIo as any).emitRoom(deviceRoomId);
    });
  };

  const selectedFacility = useRef(_.cloneDeep(selectedFacilityProp));
  const selectedStatusReports = useRef(_.cloneDeep(searchGoto.statusReports));

  const [state, setState] = useSetState<any>({
    isLoadedData: false,
    skeletonInboxes: _.map(_.range(10), () => ({})),
    recentInboxes: [],
    pageIndex: 0,
    searchValue: searchGoto.searchValue,
    searchByValue: searchGoto.searchByValue || 'patientName',
    priority: searchGoto.priority || GETTEXT_ALL_PRIORITY,
    sortValue: searchGoto.sortValue,
    isShowFacilityName: selectedFacilityProp.value === GETTEXT_ALL_FACILITIES,
    listChangingStatusReport: [],
    statusReports: searchGoto.statusReports,
  });

  /**
   * Loads the recent inbox list for the selected facility and study scope. Builds a query filter from selectedFacilityProp (omitted for the all-facilities option), the all-studies viewAll flag and searchGoto.statusReports, awaits fetchInboxes, joins the returned reports' device rooms, publishes recentUnreadCount upward, stores the formatted rows and optionally resets pageIndex; failures log, show an error toast and empty the list.
   *
   * @param isResetPageIndex - Whether to jump back to the first page after loading.
   * @returns A promise settling once state reflects the loaded rows or the failure.
   */
  const fetchData = async (isResetPageIndex = false) => {
    try {
      const queryFilter = {
        facilityId:
          selectedFacilityProp.value !== GETTEXT_ALL_FACILITIES
            ? selectedFacilityProp.value
            : undefined,
        sortOrder: 'desc',
        viewAll: selectedTypeSeePatients.value === GETTEXT_ALL_STUDIES,
        isRecentInbox: true,
        statusReports: searchGoto.statusReports,
      };
      const { reports, recentUnreadCount } = await fetchInboxes(queryFilter);
      // *: Join device rooms
      joinDeviceRooms(reports);

      recentUnreadCountRef.current = recentUnreadCount ?? 0;
      updateRecentUnreadCount(recentUnreadCount ?? 0);
      const newRecentInboxes = recentInboxesFormatter(reports);
      const setStateObject = { recentInboxes: newRecentInboxes, isLoadedData: true };
      if (isResetPageIndex) {
        _.assign(setStateObject, { pageIndex: 0 });
      }
      setState(setStateObject);
    } catch (error) {
      console.error('Failed to fetch recent inboxes: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState({
        recentInboxes: [],
        isLoadedData: true,
      });
    }
  };

  /**
   * Moves the recent inbox list to another page. Writes the requested pageIndex into state.
   *
   * @param newPageIndex - The zero-based page to display.
   * @returns No return value.
   */
  const navigatePage = (newPageIndex: any) => {
    setState({ pageIndex: newPageIndex });
  };

  /**
   * Puts the recent inbox list back into its loading state and reloads it. Clears isLoadedData, then runs the facility-scoped fetch.
   *
   * @returns No return value.
   */
  const handleReloadData = () => {
    setState({ isLoadedData: false });
    fetchData();
  };

  useImperativeHandle(ref, () => ({
    handleReloadData,
    fetchData,
  }));

  /**
   * Opens the report behind the recent inbox row the user clicked. A report of a CardioOne study raises the CardioOne report notice; any other report navigates to the `/inbox/{id}` route.
   *
   * @param clickedReport - The recent inbox row that was clicked.
   * @returns No return value.
   */
  const onClickReport = (clickedReport: any) => {
    if (isC1cStudy(clickedReport.study ?? {})) {
      emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_C1C_REPORT);
      return;
    }
    navigate(`/inbox/${clickedReport.id}`);
  };

  /**
   * Fetches one recent inbox by id and prepends it when it belongs to the facility in view. Joins its device room, keeps it only if all facilities are selected or its study facility matches, unshifts the formatted entry into a clone of recentInboxes and bumps the unread counter; a failure is logged and surfaced as an error toast.
   *
   * @param inboxId - Id of the recent inbox to fetch.
   * @returns A promise settling once the entry is prepended or the failure is reported.
   */
  const handleFetchRecentInbox = async (inboxId: any) => {
    try {
      const recentInbox = await fetchRecentInbox(inboxId);

      joinDeviceRooms([recentInbox]);

      if (
        selectedFacilityProp.value === GETTEXT_ALL_FACILITIES ||
        recentInbox?.study?.facility?.id === selectedFacilityProp.value
      ) {
        const formattedRecentInbox = recentInboxesFormatter([recentInbox]);
        const clonedInboxes = _.cloneDeep(state.recentInboxes);
        clonedInboxes.unshift(...formattedRecentInbox);
        recentUnreadCountRef.current += 1;
        updateRecentUnreadCount(recentUnreadCountRef.current);
        setState({ recentInboxes: clonedInboxes });
      }
    } catch (error) {
      console.error('Failed to fetch recent report: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    }
  };

  /**
   * Applies an inbox socket event to the recent inbox list. A recalled message removes the matching entries from a clone and decrements the unread counter by how many were dropped; otherwise a matching entry is merged with msg.inbox (raising the unread counter when it turns unread), and an unknown id triggers a single-inbox fetch unless the 'Your studies' scope excludes the report's physician for a clinic physician viewer.
   *
   * @param msg - The inbox socket event payload.
   * @returns No return value.
   */
  const handleUpdateInboxEvent = (msg: any) => {
    const clonedInboxes = _.cloneDeep(state.recentInboxes);
    if (msg.recalled) {
      /**
       * Extracts the recalled inbox entries whose id matches the message, removing them from the cloned list. Keeps the removed entries so the unread counter can be decremented by how many were dropped.
       *
       * @returns The recalled entries removed from the cloned list.
       */
      const ele = _.remove(clonedInboxes, (x) => x.id === msg.id);
      if (ele.length) {
        if (!msg.inbox?.isRead) {
          recentUnreadCountRef.current -= ele.length;
          updateRecentUnreadCount(recentUnreadCountRef.current);
        }
        setState({ recentInboxes: clonedInboxes });
      }
    } else {
      /**
       * Locates the cloned inbox entry whose id matches the incoming inbox event. Resolves to undefined when the inbox is not in the list yet, which routes the event to a single-inbox fetch instead.
       *
       * @returns The matching cloned inbox entry, or undefined.
       */
      const ele = _.find(clonedInboxes, (x) => x.id === msg.id);
      if (ele) {
        if (ele.isRead && !msg.inbox?.isRead) {
          recentUnreadCountRef.current += 1;
          updateRecentUnreadCount(recentUnreadCountRef.current);
        }
        _.assign(ele, msg.inbox);
        setState({ recentInboxes: clonedInboxes });
      } else {
        const { id, physician } = msg;
        if (selectedTypeSeePatients.value === 'Your studies') {
          if (auth.isClinicPhysician() && physician !== auth.userId()) {
            return;
          }
        }
        handleFetchRecentInbox(id);
      }
    }
  };

  /**
   * Applies a mark-inbox socket message to the matching recent inbox entry and keeps the unread badge in step. Finds the entry by id in state.recentInboxes, decrements recentUnreadCountRef and publishes it upward only when the message flips an unread entry to read, merges the message into the entry, then commits a deep clone of the list.
   *
   * @param msg - The mark-inbox socket message.
   * @returns No return value.
   */
  const handleMarkInboxEvent = (msg: any) => {
    /**
     * Locates the recent inbox entry in state whose id matches the incoming mark-inbox message. Resolves to undefined when no entry in state.recentInboxes carries that id.
     *
     * @returns The matching recent inbox entry, or undefined.
     */
    const ele = _.find(state.recentInboxes, (x) => x.id === msg.id);
    if (ele) {
      // *: Prevent unexpected decreasing unread count
      if (msg.isRead && !ele.isRead) {
        recentUnreadCountRef.current -= 1;
        updateRecentUnreadCount(recentUnreadCountRef.current);
      }
      _.assign(ele, msg);
      setState({ recentInboxes: _.cloneDeep(state.recentInboxes) });
    }
  };

  /**
   * Applies a report-updated socket message to the matching recent inbox row. Ignores messages without a status, matches on id plus an exact non-nil isNonPhi so the PHI and non-PHI variants of one report are never confused, records isRenderingBackground transitions, and clears the cached pdfArrayBuffer and url when the report just reached Ready or finished background rendering.
   *
   * @param msg - The report-updated socket message.
   * @returns No return value.
   */
  const handleReportUpdatedEvent = (msg: any) => {
    setState((prevState) => {
      if (!msg.status) return {};
      const clonedRecentInboxes = _.cloneDeep(prevState.recentInboxes);
      /**
       * Locates the cloned recent inbox matching the report-updated message by id plus an exact isNonPhi match. Requires isNonPhi to be non-nil on both sides so the PHI and non-PHI variants of one report are never confused.
       *
       * @returns The matching cloned report entry, or undefined.
       */
      const targetReport = _.find(
        clonedRecentInboxes,
        (x) =>
          x.id === msg.id &&
          !_.isNil(x.isNonPhi) &&
          !_.isNil(msg.isNonPhi) &&
          x.isNonPhi === msg.isNonPhi,
      );
      if (!targetReport) return {};
      const previousStatus = targetReport.status;
      targetReport.status = msg.status;
      let shouldReloadReportBackground = false;
      if (
        'isRenderingBackground' in msg &&
        msg.isRenderingBackground !== targetReport.isRenderingBackground
      ) {
        targetReport.isRenderingBackground = msg.isRenderingBackground;
        shouldReloadReportBackground = msg.isRenderingBackground === false;
      }
      if ((msg.status === 'Ready' && previousStatus !== 'Ready') || shouldReloadReportBackground) {
        _.assign(targetReport, { pdfArrayBuffer: undefined, url: undefined });
      }
      return { recentInboxes: clonedRecentInboxes };
    });
  };

  /**
   * Applies an incoming mark-status-report socket message to the matching recent inbox row. Deep-clones recentInboxes from previous state, locates the entry by id, keeps the change only when the message's lastStatusReportAt is newer than the stored one (or none is stored yet), then writes statusReport and lastStatusReportAt back; otherwise it returns an empty patch.
   *
   * @param msg - The mark-status-report socket message.
   * @returns No return value.
   */
  const handleMarkStatusReportEvent = (msg: any) => {
    setState((prevState) => {
      const clonedRecentInboxes = _.cloneDeep(prevState.recentInboxes);
      /**
       * Locates the cloned recent inbox entry whose id matches the mark-status-report message. Searches the deep-cloned list so the found entry can be mutated before being written back to state.
       *
       * @returns The matching cloned recent inbox entry, or undefined.
       */
      const ele = _.find(clonedRecentInboxes, (x) => x.id === msg.id);
      if (!ele) return {};
      const shouldUpdate = ele.lastStatusReportAt
        ? dayjs(msg.lastStatusReportAt).isAfter(dayjs(ele.lastStatusReportAt))
        : true;
      if (!shouldUpdate) return {};
      ele.statusReport = msg.statusReport;
      ele.lastStatusReportAt = msg.lastStatusReportAt;
      return { recentInboxes: clonedRecentInboxes };
    });
  };

  /**
   * Records the sort option picked for the recent inbox list. Writes sortValue into state, which the derived display list orders on.
   *
   * @param sortValue - The selected sort option.
   * @returns No return value.
   */
  const onChangeSort = (sortValue: any) => {
    setState({ sortValue });
  };

  /**
   * Stores the physician selected in the recent inbox filters into the shared inbox store. Spreads the existing inboxesGotoRedux.search and overwrites only physicianSearchValue through setStateRedux.
   *
   * @param physicianSearchValue - The selected physician option, or null to clear it.
   * @returns No return value.
   */
  const handleSetPhysicianSearchValue = (physicianSearchValue: any) => {
    setStateRedux({
      search: {
        ...inboxesGotoRedux.search,
        physicianSearchValue,
      },
    });
  };

  /**
   * Applies one recent inbox filter change and resets paging to the first page. searchValue collapses whitespace runs into single spaces; a changed searchByValue also clears searchValue and nulls the stored physician; physician writes the picked value into the inbox store; every other name is assigned as-is before the batched setState.
   *
   * @param name - The filter field being changed.
   * @param value - The new value for that filter field.
   * @returns No return value.
   */
  const handleChangeFilter = (name: any, value: any) => {
    const setStateObject = { pageIndex: 0 };

    switch (name) {
      case 'searchValue': {
        const searchValue = value?.replace?.(/ |\s|&nbsp;/g, ' ');
        _.assign(setStateObject, { searchValue });
        break;
      }
      case 'searchByValue': {
        if (value !== state.searchByValue) {
          // *: Refetching data if change searchByValue and has searchValue
          _.assign(setStateObject, {
            searchByValue: value,
            searchValue: undefined,
          });
          handleSetPhysicianSearchValue(null);
        }
        break;
      }
      case 'physician': {
        handleSetPhysicianSearchValue(value);
        break;
      }
      case 'statusReports': {
        _.assign(setStateObject, { [name]: value });
        break;
      }
      default: {
        _.assign(setStateObject, { [name]: value });
        break;
      }
    }

    setState(setStateObject);
  };

  useEffect(() => {
    if (!isActive) {
      const { searchByValue, searchValue, priority, sortValue, statusReports } = searchGoto;
      setState({
        searchByValue,
        searchValue,
        priority: priority || GETTEXT_ALL_PRIORITY,
        sortValue,
        pageIndex: 0,
        statusReports,
      });
    }
  }, [searchGoto]);

  useEffect(() => {
    if ((isActive && !state.isLoadedData) || isInitial.current) {
      if (isInitial.current) {
        isInitial.current = false;
      } else {
        setState({ isLoadedData: false });
      }
      fetchData();
    }
  }, [isActive]);

  useEffect(() => {
    /**
     * Registers the emitter subscription for INBOX_EVENTS messages on the recent inbox list. Holds the listener handle for cleanup and forwards each message to the inbox-update handler.
     *
     * @returns Emitter listener handle used to remove the subscription.
     */
    const inboxEventListener = emitter.addListener(EMITTER_CONSTANTS.INBOX_EVENTS, (msg: any) => {
      handleUpdateInboxEvent(msg);
    });
    return () => {
      inboxEventListener.remove();
    };
  }, [state.recentInboxes]);

  useEffect(() => {
    /**
     * Registers the emitter subscription for MARK_INBOX_EVENT messages on the recent inbox list. Holds the listener handle for cleanup and forwards each message to the mark-inbox handler.
     *
     * @returns Emitter listener handle used to remove the subscription.
     */
    const markInboxEventListener = emitter.addListener(
      EMITTER_CONSTANTS.MARK_INBOX_EVENT,
      (msg: any) => {
        handleMarkInboxEvent(msg);
      },
    );

    /**
     * Registers the emitter subscription for MARK_STATUS_REPORT messages on the recent inbox list. Holds the listener handle for cleanup and forwards each message to the status-report update handler.
     *
     * @returns Emitter listener handle used to remove the subscription.
     */
    const markStatusReportEventListener = emitter.addListener(
      EMITTER_CONSTANTS.MARK_STATUS_REPORT,
      (msg: any) => {
        handleMarkStatusReportEvent(msg);
      },
    );

    /**
     * Registers the emitter subscription for REPORT_UPDATED messages on the recent inbox list. Holds the listener handle for cleanup and forwards each message to the report-updated handler.
     *
     * @returns Emitter listener handle used to remove the subscription.
     */
    const reportUpdatedEventListener = emitter.addListener(
      EMITTER_CONSTANTS.REPORT_UPDATED,
      (msg: any) => {
        handleReportUpdatedEvent(msg);
      },
    );

    return () => {
      markInboxEventListener.remove();
      markStatusReportEventListener.remove();
      reportUpdatedEventListener.remove();
    };
  }, [state.recentInboxes]);

  useUpdateEffect(() => {
    const inboxListBodyElement = document.getElementById('inbox-list-body');
    if (inboxListBodyElement) {
      inboxListBodyElement.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  }, [state.pageIndex, state.sortValue, state.priority, state.searchValue, state.statusReports]);

  /**
   * Recent inboxes prepared for rendering, with the unread badge count recomputed and the current filters mirrored into the shared Goto search state while the tab is active. Takes a sort-only fast path when no search text, physician, priority or status filter is set, otherwise formats the search value per search-by mode before filtering and ordering.
   *
   * @returns Recent inbox rows to render for the current filters and sort.
   */
  const displayedRecentInboxes = useMemo(() => {
    const { searchValue, searchByValue, priority, sortValue, statusReports, recentInboxes } = state;

    const isSearchingByPhysician = searchByValue === 'physicianId';
    const isSearchingByStudyId = searchByValue === 'studyFid';
    const isSearchingByPatientId = searchByValue === 'patientId';

    // TODO: Check bug props.updateSearchGoto call from multiple tabs
    // *: Update search Goto
    if (isActive) {
      updateSearchGoto({
        searchValue,
        searchByValue,
        priority,
        sortValue,
        statusReports,
      });
    }

    const isFilteringAllStatusReports = _.xor(ALL_REPORT_STATUS, statusReports).length === 0;
    const { sortOrder, sortByText } = formatSortValue(sortValue);
    const formatSortOrder: any = sortByText === 'priorityRank' ? [sortOrder, 'desc'] : [sortOrder]; // Always date desc for secondary sort on priorityRank
    const formatSortBy = sortByText === 'priorityRank' ? [sortByText, 'date'] : [sortByText];

    if (
      !searchValue &&
      _.isEmpty(inboxesGotoRedux.search.physicianSearchValue) &&
      priority === GETTEXT_ALL_PRIORITY &&
      isFilteringAllStatusReports
    ) {
      const filteredRecentInboxes: any[] = _.orderBy(recentInboxes, formatSortBy, formatSortOrder);
      recentUnreadCountRef.current = _.reduce(
        filteredRecentInboxes,
        (recentUnreadCount, value) => (!value.isRead ? recentUnreadCount + 1 : recentUnreadCount),
        0,
      );
      updateRecentUnreadCount(recentUnreadCountRef.current);
      return filteredRecentInboxes;
    }

    const formattedSearchValue = isSearchingByStudyId
      ? searchStudyFidFormatter(searchValue)
      : isSearchingByPatientId
        ? searchValue?.trim().toLowerCase()
        : isSearchingByPhysician
          ? inboxesGotoRedux.search.physicianSearchValue?.value
          : searchValue;

    /**
     * Recent inboxes narrowed by priority, selected report statuses and the active search mode, then ordered by the resolved sort field and direction. Physician and study-id searches compare for equality, patient-id compares trimmed lowercase text, and any other field falls back to a lowercase substring match.
     *
     * @returns Filtered and sorted recent inbox rows.
     */
    const filteredRecentInboxes = _.orderBy(
      _.filter(recentInboxes, (recentInbox) => {
        //* Combine multiple spaces in to only one space
        const compareString = _.replace(
          String(recentInbox[searchByValue]).toLowerCase(),
          /\s\s+/g,
          ' ',
        );
        const compareSearchString = formattedSearchValue?.trim().toLowerCase();
        return (
          (priority === GETTEXT_ALL_PRIORITY ? true : recentInbox.priority === priority) &&
          _.includes(statusReports, recentInbox.statusReport) &&
          (isSearchingByPhysician
            ? formattedSearchValue
              ? recentInbox.physicianId === formattedSearchValue
              : true
            : isSearchingByStudyId
              ? formattedSearchValue
                ? _.toString(recentInbox[searchByValue]) === formattedSearchValue
                : true
              : isSearchingByPatientId
                ? formattedSearchValue
                  ? _.toString(recentInbox[searchByValue])?.trim().toLowerCase() ===
                    formattedSearchValue
                  : true
                : formattedSearchValue
                  ? _.includes(compareString, compareSearchString)
                  : true)
        );
      }),
      formatSortBy,
      formatSortOrder,
    );
    recentUnreadCountRef.current = _.reduce(
      filteredRecentInboxes,
      (recentUnreadCount, value) => (!value.isRead ? recentUnreadCount + 1 : recentUnreadCount),
      0,
    );
    updateRecentUnreadCount(recentUnreadCountRef.current);
    return filteredRecentInboxes;
  }, [
    state.recentInboxes,
    state.priority,
    state.sortValue,
    state.statusReports,
    state.searchValue,
    state.searchByValue,
    inboxesGotoRedux.search.physicianSearchValue?.value,
  ]);

  /**
   * Keeps a click on an in-row control from reaching the surrounding recent inbox row. Calls stopPropagation on the received event and does nothing else.
   *
   * @param e - The click event to stop propagating.
   * @returns No return value.
   */
  const onClickStopProg = (e: any) => {
    e.stopPropagation();
  };

  /**
   * Saves a new status report for one recent inbox and reconciles the optimistic row afterwards. Awaits handleMarkStatusReport, rolls statusReport back to the pending record's prevStatus when the save was not successful, clears isSavingStatus, drops the row from listChangingStatusReport and refetches the list; a thrown error logs, toasts and only clears isSavingStatus.
   *
   * @param id - Id of the report whose status is saved.
   * @param statusReport - The status value to persist.
   * @returns A promise settling once state reflects the saved or reverted status.
   */
  const handleUpdateStatusReport = async (id: any, statusReport: any) => {
    try {
      const result = await handleMarkStatusReport({
        id,
        statusReport,
      });
      setState((prevState) => {
        const clonedListChangingStatusReport = [...prevState.listChangingStatusReport];
        const clonedRecentInboxes = [...prevState.recentInboxes];
        /**
         * Locates the pending status-change record for this report id in the copied listChangingStatusReport. Supplies the prevStatus used to roll the row back when the save did not succeed.
         *
         * @returns The pending status-change record for the report.
         */
        const reportUpdating = _.find(clonedListChangingStatusReport, (item) => item.id === id);
        /**
         * Locates the recent inbox whose id matches the report whose status save just returned. Taken from the copied recentInboxes list so statusReport and isSavingStatus can be written back into state.
         *
         * @returns The matching recent inbox entry.
         */
        const reportUpdated = _.find(clonedRecentInboxes, (item) => item.id === id);
        if (!result.isSuccess) {
          reportUpdated.statusReport = reportUpdating.prevStatus;
        }
        reportUpdated.isSavingStatus = false;
        _.remove(clonedListChangingStatusReport, (item) => item.id === id);
        fetchData();
        return {
          listChangingStatusReport: clonedListChangingStatusReport,
          recentInboxes: clonedRecentInboxes,
        };
      });
    } catch (error) {
      console.error('Failed to update report status: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState((prevState) => {
        const clonedListChangingStatusReport = [...prevState.listChangingStatusReport];
        const clonedRecentInboxes = [...prevState.recentInboxes];
        /**
         * Locates the recent inbox matching the report id on the error path of the status save. Used to clear the isSavingStatus flag after the mutation threw.
         *
         * @returns The matching recent inbox entry.
         */
        const reportUpdated = _.find(clonedRecentInboxes, (item) => item.id === id);
        reportUpdated.isSavingStatus = false;
        return {
          listChangingStatusReport: clonedListChangingStatusReport,
          recentInboxes: clonedRecentInboxes,
        };
      });
    }
  };

  /**
   * Optimistically applies a status-report selection to one recent inbox row and starts the save. Pushes an entry carrying the row's previous status onto listChangingStatusReport, sets the row's statusReport and isSavingStatus, commits the copies to state, then delegates to the save that reverts the row when it fails.
   *
   * @param value - The newly selected status report.
   * @param id - Id of the recent inbox row being changed.
   * @returns No return value.
   */
  const onChange = (value: any, id: any) => {
    const clonedListChangingStatusReport = [...state.listChangingStatusReport];
    const clonedRecentInboxes = [...state.recentInboxes];
    /**
     * Locates the recent inbox whose id matches the row whose status dropdown changed. Taken from the copied recentInboxes so statusReport and isSavingStatus can be set optimistically.
     *
     * @returns The matching recent inbox entry.
     */
    const reportUpdated = _.find(clonedRecentInboxes, (item) => item.id === id);
    /**
     * Reads the statusReport a recent inbox held before the new selection is applied. Optional chaining yields undefined when no entry carries the given id.
     *
     * @returns The previous status report value, or undefined.
     */
    const prevStatus = _.find(clonedRecentInboxes, (item) => item.id === id)?.statusReport;
    clonedListChangingStatusReport.push({
      id,
      status: value,
      prevStatus,
      isChangingStatus: true,
    });
    reportUpdated.statusReport = value;
    reportUpdated.isSavingStatus = true;
    setState({
      listChangingStatusReport: clonedListChangingStatusReport,
      recentInboxes: clonedRecentInboxes,
    });
    handleUpdateStatusReport(id, value);
  };

  useUpdateEffect(() => {
    // Prevent double fetch first load
    if (!_.isEqual(selectedFacility.current, selectedFacilityProp)) {
      setState({
        isLoadedData: false,
        isShowFacilityName: selectedFacilityProp.value === GETTEXT_ALL_FACILITIES,
      });
      selectedFacility.current = _.cloneDeep(selectedFacilityProp);
      fetchData(true);
    }
  }, [selectedFacilityProp?.value]);

  useUpdateEffect(() => {
    handleReloadData();
    if (
      selectedTypeSeePatients.value !== GETTEXT_ALL_STUDIES &&
      state.searchByValue === 'physicianId'
    ) {
      setState({ searchByValue: 'patientName' });
      handleSetPhysicianSearchValue(null);
    }
  }, [selectedTypeSeePatients?.value]);

  useUpdateEffect(() => {
    handleSetPhysicianSearchValue(null);
  }, [selectedFacilityProp?.value]);

  useUpdateEffect(() => {
    if (!_.isEqual(selectedStatusReports.current, state.statusReports)) {
      selectedStatusReports.current = _.cloneDeep(state.statusReports);
      fetchData(true);
    }
  }, [searchGoto.statusReports]);

  useUpdateEffect(() => {
    if (
      displayedRecentInboxes.slice(
        state.pageIndex * sizePerPage.current,
        (state.pageIndex + 1) * sizePerPage.current,
      ).length === 0 &&
      state.pageIndex !== 0
    ) {
      setState({
        pageIndex: state.pageIndex - 1,
      });
    }
  }, [displayedRecentInboxes]);

  return {
    state,
    setState,
    navigate,
    inboxesGotoRedux,
    displayedRecentInboxes,
    sizePerPage,
    onChangeSort,
    handleSetPhysicianSearchValue,
    handleChangeFilter,
    onClickReport,
    onClickStopProg,
    handleUpdateStatusReport,
    onChange,
    navigatePage,
  };
};

export { useRecentInboxList };
